import type { SurveyFieldDefinition, SurveyPageDefinition } from '@engage/types';
import { Icon, ICONS } from '../../forms/ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../../forms/ui/tokens';

/**
 * Centre canvas: the selected page's questions as a lightweight, non-interactive
 * preview. Clicking a question selects it for editing. The fully interactive,
 * branching renderer is Task 4 (SurveyRenderer) — this is the builder-time
 * structural view only.
 */
export function PageCanvas(props: {
  page: SurveyPageDefinition | null;
  pageNumber: number;
  selectedFieldKey: string | null;
  onSelectField: (key: string) => void;
  onMoveField: (key: string, direction: 'up' | 'down') => void;
  onRemoveField: (key: string) => void;
}) {
  if (!props.page) {
    return (
      <Empty
        title="No page selected"
        body="Add a page from the left rail, then add questions to it."
      />
    );
  }
  if (props.page.fields.length === 0) {
    return (
      <Empty
        title={`“${props.page.label}” is empty`}
        body="Add questions from the palette on the left to build this page."
      />
    );
  }

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', fontFamily: FORM_UI_FONT }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: C.indigoSoft, marginBottom: 12 }}>
        Page {props.pageNumber} · {props.page.label}
      </div>
      {props.page.fields.map((field, i) => {
        const active = field.key === props.selectedFieldKey;
        return (
          <div
            key={field.key}
            onClick={() => props.onSelectField(field.key)}
            style={{
              background: '#fff',
              borderRadius: 12,
              border: `1px solid ${active ? C.indigo : C.line}`,
              boxShadow: active ? '0 0 0 3px rgba(27,31,107,.08)' : 'none',
              padding: '14px 16px',
              marginBottom: 10,
              cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: C.ink }}>
                  {field.label}
                  {field.required && <span style={{ color: C.danger, marginLeft: 4 }}>*</span>}
                </div>
                {field.help_text && (
                  <div style={{ fontSize: 11.5, color: C.faint, marginTop: 2 }}>{field.help_text}</div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                <TypeTag field={field} />
                <CanvasBtn disabled={i === 0} title="Move up" onClick={(e) => { e.stopPropagation(); props.onMoveField(field.key, 'up'); }}>↑</CanvasBtn>
                <CanvasBtn disabled={i === props.page!.fields.length - 1} title="Move down" onClick={(e) => { e.stopPropagation(); props.onMoveField(field.key, 'down'); }}>↓</CanvasBtn>
                <CanvasBtn danger title="Remove" onClick={(e) => { e.stopPropagation(); props.onRemoveField(field.key); }}>
                  <Icon d={ICONS.x} size={12} color={C.danger} sw={2.4} />
                </CanvasBtn>
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <FieldPreview field={field} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TypeTag({ field }: { field: SurveyFieldDefinition }) {
  const label = TYPE_LABELS[field.type];
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: field.contact_key ? C.tealDark : C.faint,
        background: field.contact_key ? C.contactBg : C.bg,
        border: `1px solid ${field.contact_key ? C.teal : C.line}`,
        borderRadius: 6,
        padding: '2px 7px',
        whiteSpace: 'nowrap',
        marginRight: 4,
      }}
      title={field.contact_key ? `Tagged as contact ${field.contact_key}` : undefined}
    >
      {field.contact_key ? `◆ ${field.contact_key}` : label}
    </span>
  );
}

function FieldPreview({ field }: { field: SurveyFieldDefinition }) {
  const box: React.CSSProperties = {
    width: '100%',
    border: `1px solid ${C.line}`,
    borderRadius: 8,
    padding: '8px 10px',
    fontSize: 12.5,
    color: C.faint,
    background: C.bg,
    boxSizing: 'border-box',
  };
  switch (field.type) {
    case 'LONG_TEXT':
      return <div style={{ ...box, height: 52 }}>Long answer…</div>;
    case 'SHORT_TEXT':
    case 'EMAIL':
    case 'PHONE':
    case 'NUMBER':
    case 'DATE':
      return <div style={box}>{PLACEHOLDERS[field.type]}</div>;
    case 'SINGLE_SELECT':
    case 'MULTI_SELECT':
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {field.options.map((o) => (
            <span
              key={o.value}
              style={{
                fontSize: 12,
                color: C.sub,
                border: `1px solid ${C.line}`,
                borderRadius: field.type === 'MULTI_SELECT' ? 6 : 999,
                padding: '5px 11px',
                background: '#fff',
              }}
            >
              {o.label}
            </span>
          ))}
        </div>
      );
    case 'NUMERIC_SCALE': {
      const steps = scaleSteps(field.min, field.max, field.step ?? 1);
      return (
        <div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {steps.map((n) => (
              <span key={n} style={pill}>
                {n}
              </span>
            ))}
          </div>
          {(field.min_label || field.max_label) && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: C.faint, marginTop: 4 }}>
              <span>{field.min_label}</span>
              <span>{field.max_label}</span>
            </div>
          )}
        </div>
      );
    }
    case 'LIKERT':
      return (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {field.options.map((o) => (
            <span key={o.value} style={pill}>
              {o.label}
            </span>
          ))}
        </div>
      );
    case 'NPS':
      return (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {Array.from({ length: 11 }, (_, n) => (
            <span key={n} style={{ ...pill, minWidth: 22, textAlign: 'center' }}>
              {n}
            </span>
          ))}
        </div>
      );
    case 'CONSENT_CHECKBOX':
      return (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: C.sub }}>
          <input type="checkbox" disabled />
          {field.text_override || field.label}
        </label>
      );
  }
}

const pill: React.CSSProperties = {
  fontSize: 12,
  color: C.sub,
  border: `1px solid ${C.line}`,
  borderRadius: 8,
  padding: '5px 10px',
  background: '#fff',
};

function scaleSteps(min: number, max: number, step: number): number[] {
  const out: number[] = [];
  const s = step > 0 ? step : 1;
  for (let n = min; n <= max && out.length < 30; n += s) out.push(n);
  return out;
}

function CanvasBtn(props: {
  children: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  danger?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      title={props.title}
      disabled={props.disabled}
      onClick={props.onClick}
      style={{
        width: 22,
        height: 22,
        borderRadius: 6,
        border: `1px solid ${C.line}`,
        background: '#fff',
        color: props.disabled ? C.line : props.danger ? C.danger : C.sub,
        fontSize: 12,
        lineHeight: 1,
        cursor: props.disabled ? 'default' : 'pointer',
        fontFamily: FORM_UI_FONT,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {props.children}
    </button>
  );
}

function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div
      style={{
        maxWidth: 520,
        margin: '40px auto',
        background: '#fff',
        borderRadius: 16,
        border: `2px dashed ${C.line}`,
        padding: '56px 32px',
        textAlign: 'center',
        fontFamily: FORM_UI_FONT,
      }}
    >
      <Icon d={ICONS.layers} size={34} color={C.faint} />
      <div style={{ fontSize: 14, fontWeight: 600, color: C.indigoSoft, marginTop: 14 }}>{title}</div>
      <div style={{ fontSize: 12, color: C.faint, marginTop: 6 }}>{body}</div>
    </div>
  );
}

const TYPE_LABELS: Record<SurveyFieldDefinition['type'], string> = {
  SHORT_TEXT: 'Text',
  LONG_TEXT: 'Long',
  EMAIL: 'Email',
  PHONE: 'Phone',
  NUMBER: 'Number',
  DATE: 'Date',
  SINGLE_SELECT: 'Select',
  MULTI_SELECT: 'Multi',
  NUMERIC_SCALE: 'Scale',
  LIKERT: 'Likert',
  NPS: 'NPS',
  CONSENT_CHECKBOX: 'Consent',
};

const PLACEHOLDERS: Record<'SHORT_TEXT' | 'EMAIL' | 'PHONE' | 'NUMBER' | 'DATE', string> = {
  SHORT_TEXT: 'Short answer…',
  EMAIL: 'name@example.com',
  PHONE: '+61 …',
  NUMBER: '0',
  DATE: 'dd / mm / yyyy',
};
